// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
  output,
} from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import { LocalizedDatePipe } from '../../shared/pipes/localizedDate.pipe';
import { PartStatusInfoComponent } from '../part-status-info/part-status-info.component';
import { ClipboardService } from '../../shared/services/clipboard.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SpPartsModel } from '../interfaces/sp-parts-model';
import { SpSortEventModel } from '../model/sp-sort-event-model';

type SparePartsColumnsType = {
  columns: number;
  minWidth: string;
  label: string;
  property: keyof SpPartsModel;
};

@Component({
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    LocalizedDatePipe,
    MatTooltipModule,
    PartStatusInfoComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-service-parts',
  templateUrl: './service-parts.component.html',
  styleUrl: './service-parts.component.scss',
})
export class ServicePartsComponent {
  parts = input<SpPartsModel[]>([]);
  expanded = input<boolean>(false);
  sortChange = output<SpSortEventModel>();
  protected readonly clipboardService = inject(ClipboardService);

  private readonly shortColumns: SparePartsColumnsType[] = [
    {
      columns: 5,
      minWidth: '108px',
      label: 'spareParts.created',
      property: 'creationDate',
    },
    {
      columns: 8,
      minWidth: '156px',
      label: 'spareParts.ticketNumber',
      property: 'ticketNumber',
    },
    {
      columns: 12,
      minWidth: '125px',
      label: 'spareParts.partDescription',
      property: 'partDescription',
    },
    {
      columns: 5,
      minWidth: '140px',
      label: 'spareParts.partNumber',
      property: 'partNumber',
    },
    {
      columns: 8,
      minWidth: '156px',
      label: 'spareParts.partStatus',
      property: 'partStatus',
    },
  ];

  private readonly expandedColumns: SparePartsColumnsType[] = [
    {
      columns: 5,
      minWidth: '108px',
      label: 'spareParts.created',
      property: 'creationDate',
    },
    {
      columns: 8,
      minWidth: '156px',
      label: 'spareParts.ticketNumber',
      property: 'ticketNumber',
    },
    {
      columns: 12,
      minWidth: '125px',
      label: 'spareParts.partDescription',
      property: 'partDescription',
    },
    {
      columns: 5,
      minWidth: '120px',
      label: 'spareParts.partNumberShort',
      property: 'partNumber',
    },
    {
      columns: 6,
      minWidth: '130px',
      label: 'spareParts.ticketType',
      property: 'ticketType',
    },
    {
      columns: 6,
      minWidth: '140px',
      label: 'spareParts.ticketStatus',
      property: 'ticketStatus',
    },
    {
      columns: 5,
      minWidth: '120px',
      label: 'spareParts.quantityShort',
      property: 'quantity',
    },
    {
      columns: 8,
      minWidth: '156px',
      label: 'spareParts.partStatus',
      property: 'partStatus',
    },
  ];

  protected readonly columnsDefinition = computed(() =>
    this.expanded() ? this.expandedColumns : this.shortColumns,
  );

  protected hasOverflow = (element: HTMLElement): boolean => {
    return element.scrollWidth > element.clientWidth;
  };

  protected toggleSort(property: string, isDescending: boolean): void {
    this.sortChange.emit({ property, isDescending });
  }
}
